import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getArtifactProjectId } from '../firebaseInit';
import { useNotification } from '../contexts/NotificationContext';
import { 
  loadTeamsAndUsers, 
  createTeam, 
  inviteUser, 
  updateUserTemplate, 
  bulkAssignTemplate,
  updateUserRole,
  removeUserFromTeam,
  findUserTeam,
  deleteTeam,
  getRoleDisplayName,
  getStatusDisplayName,
  getStatusBadge,
  getRoleBadge,
  getActualUserStatus
} from '../utils/teamManagement';
import * as firestorePaths from '../utils/firestorePaths';
import TeamForm from './shared/TeamForm';
import UserInviteForm from './shared/UserInviteForm';
import TemplateSelector from './shared/TemplateSelector';
import Button from './shared/Button';
import LoadingSpinner from './shared/LoadingSpinner';
import SubscriptionsAdmin from './SubscriptionsAdmin';
import PricingManager from './PricingManager';
import { doc as firestoreDoc, getDoc } from 'firebase/firestore';

const AdminPanel = ({
  onClose, userRole, userTeam, db, firebaseConfig, getEmailDocumentId, userEmail, isAdmin, canManageUsers, availableScripts = {},
  onShowProblemAnalyzer, onShowAdminTemplateBrowser, toggleMaintenance, maintenanceEnabled, isUpdatingMaintenance
}) => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [showPricingManager, setShowPricingManager] = useState(false);

  const isTestEnvironment = firebaseConfig?.projectId === 'treegenie-test';
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    if (!isTestEnvironment || !isAdmin || !db) return;
    const fetchSyncStatus = async () => {
      try {
        const syncDoc = await getDoc(firestoreDoc(db, 'artifacts/my-real-estate-assistant/sync-status/test'));
        if (syncDoc.exists()) setSyncStatus(syncDoc.data());
      } catch (err) { /* silently fail */ }
    };
    fetchSyncStatus();
  }, [isTestEnvironment, isAdmin, db]);

  const getTimeSince = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown';
    const diffMs = Date.now() - timestamp.toDate().getTime();
    const mins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  }, []);
    
  // Initialize Firebase Functions
  const functions = getFunctions();
  const refreshUserClaimsByEmail = httpsCallable(functions, 'refreshUserClaimsByEmail');
    
  // Use notification system (replaces alert())
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
    
  // Template assignment state
  const [showTemplateAssignment, setShowTemplateAssignment] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkTemplate, setBulkTemplate] = useState('example-script');
    
  // Filter state
  const [filters, setFilters] = useState({
    email: '',
    role: '',
    status: '',
    team: ''
  });
    
  // Team management state
  const [selectedTeamForManagement, setSelectedTeamForManagement] = useState(null);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
    
  // Warning dialog state
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningAction, setWarningAction] = useState(null);
  const [warningData, setWarningData] = useState({});
    
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState({});

  // Remove/Deactivate confirmation state
  const [userToRemove, setUserToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Load users and teams
  const loadData = useCallback(async () => {
    if (!db) return;
        
    setLoading(true);
    try {
      const { teams: teamsData, users: usersData } = await loadTeamsAndUsers(db, firebaseConfig, 'admin', userEmail);
      setTeams(teamsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users and teams:', error);
    } finally {
      setLoading(false);
      setHasInitialized(true);
    }
  }, [db, firebaseConfig, userEmail]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Warning dialog functions
  const showTemplateWarning = (action, data) => {
    setWarningAction(action);
    setWarningData(data);
    setShowWarningDialog(true);
  };

  const executeWarningAction = async () => {
    setShowWarningDialog(false);
        
    switch (warningAction) {
      case 'updateUserTemplate':
        await handleUpdateUserTemplate(warningData.userId, warningData.newTemplate);
        break;
      case 'bulkAssignTemplate':
        await handleBulkAssignTemplate();
        break;
      default:
        break;
    }
  };

  // Confirmation dialog functions
  const showConfirmDialogAction = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmDialog(true);
  };

  const executeConfirmAction = async () => {
    setShowConfirmDialog(false);
        
    switch (confirmAction) {
      case 'deleteTeam':
        await handleDeleteTeam(confirmData.teamId);
        break;
      default:
        break;
    }
  };

  // Create new team
  const handleCreateTeam = async (teamName) => {
    try {
      await createTeam(db, firebaseConfig, teamName, userEmail);
      loadData();
    } catch (error) {
      console.error('Error creating team:', error);
    }
  };

  // Invite user to team
  const handleInviteUser = async (userData) => {
    try {
      // Normalize email immediately to prevent case sensitivity bugs
      const normalizedEmail = userData.email.toLowerCase().trim();
            
      // Check if user already exists and their status - PREVENT duplicate invitations
      const emailDocId = getEmailDocumentId(normalizedEmail);
      const existingUser = users.find(u => u.id === emailDocId);
            
      if (existingUser) {
        const actualStatus = getActualUserStatus(existingUser);
                
        if (actualStatus === 'active') {
          // Block inviting active users
          showError(
            `User ${normalizedEmail} is already an active team member. To change their role or template, use the table below.`,
            { duration: 6000 }
          );
          return;
        } else if (actualStatus === 'invited') {
          // Block inviting already-invited users
          showWarning(
            `User ${normalizedEmail} already has a pending invitation. They can sign up using the invitation that was previously sent.`,
            { duration: 6000 }
          );
          return;
        }
      }
            
      // Add the invitedBy field to track who sent the invitation
      const userDataWithInviter = {
        ...userData,
        email: normalizedEmail,
        invitedBy: userEmail
      };
            
      await inviteUser(db, firebaseConfig, userDataWithInviter, getEmailDocumentId);
      loadData();
            
      showSuccess(`Invitation sent to ${normalizedEmail}! They can sign up using your team lead email.`, { duration: 5000 });
    } catch (error) {
      console.error('Error inviting user:', error);
      showError(`Failed to invite user. ${error.message}`);
    }
  };

  // Update user role
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateUserRole(db, firebaseConfig, userId, newRole);
      loadData();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  // Update user template
  const handleUpdateUserTemplate = async (userId, newTemplate) => {
    try {
      await updateUserTemplate(db, firebaseConfig, userId, newTemplate);
      const user = users.find(u => u.id === userId);
      const templateName = availableScripts[newTemplate]?.name || 'No Template';
      showSuccess(`Template updated to "${templateName}" for ${user.email}`);
      loadData();
    } catch (error) {
      console.error('Error updating user template:', error);
      showError(`Failed to update template. ${error.message}`);
    }
  };


  // Confirm before removing user from team
  const confirmRemoveUser = (user) => {
    setUserToRemove(user);
    setShowRemoveConfirm(true);
  };

  // Remove user from team (after confirmation)
  const handleRemoveUserFromTeam = async () => {
    if (!userToRemove) return;
    try {
      setShowRemoveConfirm(false);
      setLoading(true);
      await removeUserFromTeam(db, firebaseConfig, userToRemove.id);
      showSuccess(`Successfully removed ${userToRemove.email || 'user'} from the team.`);
      loadData();
    } catch (error) {
      console.error('Error removing user from team:', error);
      showError('Failed to remove user from team. Please try again.');
    } finally {
      setUserToRemove(null);
      setLoading(false);
    }
  };

  // Confirm before deactivating user
  const confirmDeactivateUser = (user) => {
    setUserToDeactivate(user);
    setShowDeactivateConfirm(true);
  };

  // Execute deactivation (after confirmation)
  const executeDeactivateUser = async () => {
    if (!userToDeactivate) return;
    setShowDeactivateConfirm(false);
    await handleDeactivateUser(userToDeactivate.id);
    setUserToDeactivate(null);
  };

  // Deactivate user
  const handleDeactivateUser = async (userId) => {
    try {
      setLoading(true);
            
      const appId = getArtifactProjectId();
      const user = users.find(u => u.id === userId);
      if (!user) {
        showError('User not found!');
        return;
      }
            
      // Find user's team and update their status to inactive
      const teamId = await findUserTeam(db, appId, userId, userTeam);
      if (!teamId) {
        showError('User\'s team not found!');
        return;
      }
            
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
      await setDoc(teamUserRef, {
        status: 'inactive',
        deactivatedAt: new Date(),
        deactivatedBy: userEmail
      }, { merge: true });
            
      // Immediately refresh the user's custom claims to ensure they're signed out
      try {
        console.log('🔄 Refreshing custom claims for deactivated user:', user.email);
        await refreshUserClaimsByEmail({ userEmail: user.email });
        console.log('✅ Custom claims refreshed successfully');
      } catch (claimsError) {
        console.error('❌ Error refreshing claims:', claimsError);
        // Don't fail the deactivation if claims refresh fails
      }
            
      showSuccess(`User ${user.email} deactivated. They will be signed out automatically.`, { duration: 5000 });
      await loadData();
    } catch (error) {
      console.error('Error deactivating user:', error);
      showError(`Failed to deactivate user. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async (userId) => {
    try {
      setLoading(true);
            
      const appId = getArtifactProjectId();
      const user = users.find(u => u.id === userId);
      if (!user) {
        showError('User not found!');
        return;
      }
            
      // Find user's team and update their status to active
      const teamId = await findUserTeam(db, appId, userId, userTeam);
      if (!teamId) {
        showError('User\'s team not found!');
        return;
      }
            
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
      await setDoc(teamUserRef, {
        status: 'active',
        reactivatedAt: new Date(),
        reactivatedBy: userEmail
      }, { merge: true });
            
      // Immediately refresh the user's custom claims to restore access
      try {
        console.log('🔄 Refreshing custom claims for reactivated user:', user.email);
        await refreshUserClaimsByEmail({ userEmail: user.email });
        console.log('✅ Custom claims refreshed successfully');
      } catch (claimsError) {
        console.error('❌ Error refreshing claims:', claimsError);
        // Don't fail the reactivation if claims refresh fails
      }
            
      showSuccess(`User ${user.email} reactivated. They can log in now.`, { duration: 5000 });
      await loadData();
    } catch (error) {
      console.error('Error reactivating user:', error);
      showError(`Failed to reactivate user. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add user to team
  const handleAddUserToTeam = async (userId, teamId) => {
    try {
      // Team assignment is now handled via Custom Claims and team containers
      // Legacy user document updates are no longer needed
      console.log('⚠️ Team assignment now requires Custom Claims update via Cloud Functions');
      console.log(`Would assign user ${userId} to team ${teamId}`);
            
      loadData();
    } catch (error) {
      console.error('Error adding user to team:', error);
    }
  };



  // Bulk assign template to selected users
  const handleBulkAssignTemplate = async () => {
    if (selectedUsers.length === 0) {
      showWarning('Please select at least one user to assign a template to.');
      return;
    }

    try {
      await bulkAssignTemplate(db, firebaseConfig, selectedUsers, bulkTemplate);
            
      const templateName = availableScripts[bulkTemplate]?.name || 'No Template';
      showSuccess(`Template "${templateName}" assigned to ${selectedUsers.length} user(s)!`);
            
      setSelectedUsers([]);
      setBulkTemplate('example-script');
      setShowTemplateAssignment(false);
      loadData();
    } catch (error) {
      console.error('Error bulk assigning template:', error);
      showError(`Failed to assign template. ${error.message}`);
    }
  };

  // Toggle user selection for bulk operations
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Delete team
  const handleDeleteTeam = async (teamId) => {
    try {
      // Show loading state
      setLoading(true);
            
      await deleteTeam(db, firebaseConfig, teamId, users);
            
      // Show success message
      showSuccess('Team deleted successfully!');
            
      // Reload data
      await loadData();
    } catch (error) {
      console.error('Error deleting team:', error);
      showError(`Failed to delete team. ${error.message}`);
    } finally {
      // Always hide loading state
      setLoading(false);
    }
  };



  // Filter users based on current filters
  const filteredUsers = users.filter(user => {
    const emailMatch = !filters.email || user.email.toLowerCase().includes(filters.email.toLowerCase());
    const roleMatch = !filters.role || user.role === filters.role;
    const statusMatch = !filters.status || getActualUserStatus(user) === filters.status;
    const teamMatch = !filters.team || (user.teamId && teams.find(t => t.id === user.teamId)?.name === filters.team);
        
    return emailMatch && roleMatch && statusMatch && teamMatch;
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      email: '',
      role: '',
      status: '',
      team: ''
    });
  };

  // Get unique values for filter dropdowns
  const uniqueRoles = [...new Set(users.map(u => u.role))];
  const uniqueStatuses = [...new Set(users.map(u => getActualUserStatus(u)))];
  const uniqueTeams = [...new Set(teams.map(t => t.name))];











  return (
    <>
      <div className="bg-white rounded-lg p-4 sm:p-4 w-full h-[90vh] overflow-y-auto min-w-0">

        {(loading || !hasInitialized) && (
          <div className="text-center py-4">
            <LoadingSpinner size="medium" message="Loading..." />
          </div>
        )}

        {/* Main Content - Only show when not loading and initialized */}
        {!loading && hasInitialized && (
          <>
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-3 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'users'
                    ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                }`}
              >
                Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('teams')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'teams'
                    ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                }`}
              >
                Teams ({teams.length})
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('subscriptions')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'subscriptions'
                      ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  Subscriptions
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('tools')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'tools'
                      ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  Admin Tools
                </button>
              )}
            </div>

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Invite User */}
                  <UserInviteForm
                    onSubmit={handleInviteUser}
                    teams={teams}
                    availableScripts={availableScripts}
                    showRoleSelector={true}
                    submitLabel="Invite User"
                    currentUserRole={userRole}
                  />



                  {/* Bulk Template Assignment - Only show for Team Leads, not Admins */}
                  {canManageUsers() && !isAdmin && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-3">Bulk Template Assignment</h4>
                      <div className="space-y-3">
                        <Button
                          variant="primary"
                          size="md"
                          fullWidth
                          onClick={() => setShowTemplateAssignment(!showTemplateAssignment)}
                        >
                          {showTemplateAssignment ? 'Hide' : 'Show'} Bulk Assignment
                        </Button>
                                        
                        {showTemplateAssignment && (
                          <div className="space-y-3">
                            <TemplateSelector
                              value={bulkTemplate}
                              onChange={(e) => setBulkTemplate(e.target.value)}
                              availableScripts={availableScripts}
                            />
                            <Button
                              variant="primary"
                              size="md"
                              fullWidth
                              onClick={() => showTemplateWarning('bulkAssignTemplate', {})}
                              disabled={selectedUsers.length === 0}
                            >
                                                    Assign to {selectedUsers.length} Selected User(s)
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* For Admins, show a note about template management */}
                  {isAdmin && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-2">Template Management</h4>
                      <p className="text-sm text-gray-600">
                                        As an Admin, you can view all user templates and their assignments. 
                                        Template assignment is managed by Team Leads for their team members.
                      </p>
                    </div>
                  )}
                </div>

                {/* Filter Controls */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <h4 className="font-medium text-gray-700">Filter Users</h4>
                                
                    {/* Email Filter */}
                    <div className="flex-1 min-w-48">
                      <input
                        type="text"
                        placeholder="Filter by email..."
                        value={filters.email}
                        onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                                
                    {/* Role Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.role}
                        onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Roles</option>
                        {uniqueRoles.map(role => (
                          <option key={role} value={role}>
                            {getRoleDisplayName(role)}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Status Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Statuses</option>
                        {uniqueStatuses.map(status => (
                          <option key={status} value={status}>
                            {getStatusDisplayName(status)}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Team Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.team}
                        onChange={(e) => setFilters(prev => ({ ...prev, team: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Teams</option>
                        {uniqueTeams.map(team => (
                          <option key={team} value={team}>
                            {team}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Clear Filters Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                    >
                                    Clear Filters
                    </Button>
                                
                    {/* Results Count */}
                    <div className="text-sm text-gray-500">
                                    Showing {filteredUsers.length} of {users.length} users
                    </div>
                  </div>
                            
                  {/* No Results Message */}
                  {filteredUsers.length === 0 && users.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center space-x-2">
                        <div className="text-yellow-600">🔍</div>
                        <div className="text-yellow-800">
                          <strong>No users found</strong> with the current filters. 
                                            Try adjusting your search criteria or 
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="underline ml-1"
                          >
                                                clear all filters
                          </Button>.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Users Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {showTemplateAssignment && !isAdmin && (
                            <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <input
                                type="checkbox"
                                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers(filteredUsers.map(u => u.id));
                                  } else {
                                    setSelectedUsers([]);
                                  }
                                }}
                                className="rounded border-gray-300"
                              />
                            </th>
                          )}
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {isAdmin ? 'User\'s Templates' : 'Template'}
                          </th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            {showTemplateAssignment && !isAdmin && (
                              <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={() => toggleUserSelection(user.id)}
                                  className="rounded border-gray-300"
                                />
                              </td>
                            )}
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{user.email}</div>
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              <select
                                value={user.role || 'basic'}
                                onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                                disabled={!canManageUsers()}
                              >
                                <option value="basic">Basic</option>
                                <option value="team_lead">Team Lead</option>
                                {isAdmin && <option value="admin">Admin</option>}
                              </select>
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              {user.teamId ? (
                                <div className="text-sm text-gray-900">
                                  {teams.find(t => t.id === user.teamId)?.name || 'Unknown'}
                                </div>
                              ) : (
                                <select
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value === 'new') {
                                      // Show team creation form
                                      setShowTeamManagement(true);
                                      setSelectedTeamForManagement({ type: 'create', userId: user.id });
                                    } else if (e.target.value) {
                                      handleAddUserToTeam(user.id, e.target.value);
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  disabled={!canManageUsers()}
                                >
                                  <option value="">Assign to Team</option>
                                  {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                      {team.name}
                                    </option>
                                  ))}
                                  <option value="new">+ Create New Team</option>
                                </select>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              {/* Show different template view for Admins vs Team Leads */}
                              {isAdmin ? (
                              /* Admin View: Show User's Templates */
                                <div className="text-sm">
                                  <div className="font-medium text-gray-900 mb-1">
                                                                Current: {availableScripts[user.defaultTemplate || 'example-script']?.name || 'Example Script (Demo)'}
                                  </div>
                                  {user.teamId && (
                                    (() => {
                                      const team = teams.find(t => t.id === user.teamId);
                                      const teamLeadTemplates = Object.entries(availableScripts)
                                        .filter(([_, script]) => script.creatorId === team?.leadEmail)
                                        .map(([_, script]) => script.name);
                                                                    
                                      if (teamLeadTemplates.length === 0) {
                                        return (
                                          <div className="text-xs text-gray-500">
                                                                                Saved Templates: None created yet
                                          </div>
                                        );
                                      }
                                                                    
                                      return (
                                        <details className="text-xs text-gray-500">
                                          <summary className="cursor-pointer hover:text-gray-700 select-none">
                                                                                Saved Templates ({teamLeadTemplates.length})
                                          </summary>
                                          <div className="mt-1 ml-2 space-y-1">
                                            {teamLeadTemplates.map((templateName, index) => (
                                              <div key={index} className="text-gray-600">
                                                                                        • {templateName}
                                              </div>
                                            ))}
                                          </div>
                                        </details>
                                      );
                                    })()
                                  )}
                                  {!user.teamId && (
                                    <div className="text-xs text-gray-500">
                                                                    No team assigned
                                    </div>
                                  )}
                                </div>
                              ) : (
                              /* Team Lead View: Show template assignment dropdown */
                                <select
                                  value={user.defaultTemplate || 'example-script'}
                                  onChange={(e) => showTemplateWarning('updateUserTemplate', {
                                    userId: user.id,
                                    newTemplate: e.target.value
                                  })}
                                  className="text-sm border border-gray-300 rounded px-2 py-1"
                                  disabled={!canManageUsers()}
                                >
                                  <option value="example-script">Example Script (Demo)</option>
                                  {Object.entries(availableScripts)
                                    .filter(([id]) => id !== 'example-script')
                                    .map(([id, script]) => (
                                      <option key={id} value={id}>
                                        {script.name}
                                      </option>
                                    ))}
                                </select>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                {(() => {
                                  const actualStatus = getActualUserStatus(user);
                                  return (
                                    <>
                                      <span className={getStatusBadge(actualStatus)}>
                                        {getStatusDisplayName(actualStatus)}
                                      </span>
                                      {actualStatus === 'invited' && user.invitedBy && (
                                        <div className="text-xs text-gray-500">
                                                                            Invited by: {user.invitedBy}
                                        </div>
                                      )}
                                      {actualStatus === 'active' && user.invitedBy && (
                                        <div className="text-xs text-gray-500">
                                                                            Invited by: {user.invitedBy}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex flex-col space-y-1">
                                {user.teamId && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => confirmRemoveUser(user)}
                                    disabled={!canManageUsers()}
                                    className="text-red-600 text-xs"
                                  >
                                                                Remove from Team
                                  </Button>
                                )}
                                {getActualUserStatus(user) === 'inactive' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReactivateUser(user.id)}
                                    disabled={!canManageUsers()}
                                    className="text-green-600 text-xs"
                                  >
                                                                Reactivate User
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => confirmDeactivateUser(user)}
                                    disabled={!canManageUsers()}
                                    className="text-orange-600 text-xs"
                                  >
                                                                Deactivate User
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === 'teams' && (
              <div className="space-y-4">
                {/* Create Team */}
                <TeamForm 
                  onSubmit={handleCreateTeam}
                />

                {/* Teams Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map(team => (
                    <div 
                      key={team.id} 
                      className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      onClick={() => {
                        setSelectedTeamForManagement({ type: 'manage', team });
                        setShowTeamManagement(true);
                      }}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium text-gray-800">{team.name}</h4>
                          <p className="text-sm text-gray-600">Lead: {team.leadEmail}</p>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              showConfirmDialogAction('deleteTeam', { teamId: team.id });
                            }}
                            className="text-red-600"
                          >
                                                Delete
                          </Button>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        <p>Members: {users.filter(u => u.teamId === team.id).length}</p>
                        <p>Created: {team.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</p>
                      </div>
                      <div className="mt-3 text-xs text-blue-600">
                                        Click to manage team
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscriptions Tab (Admin Only) */}
            {activeTab === 'subscriptions' && isAdmin && (
              <SubscriptionsAdmin
                teams={teams}
                users={users}
                db={db}
                onRefresh={loadData}
              />
            )}

            {/* Admin Tools Tab */}
            {activeTab === 'tools' && isAdmin && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={onShowProblemAnalyzer}
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="bg-blue-100 p-2 rounded">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Feedback Analyzer</div>
                      <div className="text-sm text-gray-600">View and analyze user feedback and bug reports</div>
                    </div>
                  </button>

                  <button
                    onClick={toggleMaintenance}
                    disabled={isUpdatingMaintenance}
                    className={`flex items-center space-x-3 p-4 border rounded-lg transition-colors ${
                      maintenanceEnabled 
                        ? 'border-red-300 bg-red-50 hover:bg-red-100' 
                        : 'border-gray-200 hover:bg-gray-50'
                    } ${isUpdatingMaintenance ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`p-2 rounded ${maintenanceEnabled ? 'bg-red-100' : 'bg-orange-100'}`}>
                      <svg className={`w-5 h-5 ${maintenanceEnabled ? 'text-red-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">
                        {isUpdatingMaintenance ? 'Updating...' : maintenanceEnabled ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {maintenanceEnabled ? 'Currently blocking non-admin access' : 'Currently allowing all users to access the app'}
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={onShowAdminTemplateBrowser}
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="bg-blue-100 p-2 rounded">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Template Browser</div>
                      <div className="text-sm text-gray-600">Browse and export templates from all teams</div>
                    </div>
                  </button>

                  <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="bg-blue-100 p-2 rounded">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-500">More tools coming soon</div>
                      <div className="text-sm text-gray-400">Performance analytics, user behavior, etc.</div>
                    </div>
                  </div>
                </div>

                {/* Test Environment Sync Status */}
                {isTestEnvironment && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                    <span className="text-amber-600 text-lg">⚠️</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-amber-800">Test Environment</div>
                      <div className="text-xs text-amber-700">
                        {syncStatus?.lastSyncAt
                          ? <>Data synced from production: <strong>{getTimeSince(syncStatus.lastSyncAt)}</strong>{syncStatus.documentsImported && <span className="ml-1 opacity-75">({syncStatus.documentsImported.toLocaleString()} docs)</span>}</>
                          : 'Sync status unknown'}
                      </div>
                    </div>
                    <a
                      href="https://treegenie.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-amber-800 underline hover:text-amber-900"
                    >
                      Go to Production
                    </a>
                  </div>
                )}

                {/* Pricing Manager */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  {!showPricingManager ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Pricing Management</h3>
                        <p className="text-sm text-gray-500">Edit subscription tiers and add-on pricing</p>
                      </div>
                      <button
                        onClick={() => setShowPricingManager(true)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Edit Pricing
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-amber-700 font-medium">Editing mode — changes affect all users</span>
                        <button
                          onClick={() => setShowPricingManager(false)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Close Editor
                        </button>
                      </div>
                      <PricingManager />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

      </div>

      {/* Team Management Modal */}
      {showTeamManagement && selectedTeamForManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') { setShowTeamManagement(false); setSelectedTeamForManagement(null); } }}>
          <div className="bg-white rounded-lg p-4 sm:p-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="team-mgmt-dialog-title">
            <div className="flex justify-between items-center mb-4">
              <h3 id="team-mgmt-dialog-title" className="text-lg font-semibold text-gray-800">
                {selectedTeamForManagement.type === 'create' ? 'Create New Team' : `Manage Team: ${selectedTeamForManagement.team.name}`}
              </h3>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowTeamManagement(false);
                  setSelectedTeamForManagement(null);
                }}
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
                aria-label="Close"
              />
            </div>

            {selectedTeamForManagement.type === 'create' ? (
            /* Create New Team Form */
              <div className="space-y-4">
                <TeamForm 
                  onSubmit={async (teamName) => {
                    try {
                      const teamId = await createTeam(db, firebaseConfig, teamName, userEmail);
                      // Add the user to the newly created team
                      await handleAddUserToTeam(selectedTeamForManagement.userId, teamId);
                      setShowTeamManagement(false);
                      setSelectedTeamForManagement(null);
                    } catch (error) {
                      console.error('Error creating team:', error);
                    }
                  }}
                />
              </div>
            ) : (
            /* Team Management View */
              <div className="space-y-4">
                {/* Team Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-3">Team Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Name:</span> {selectedTeamForManagement.team.name}
                    </div>
                    <div>
                      <span className="font-medium">Lead:</span> {selectedTeamForManagement.team.leadEmail}
                    </div>
                    <div>
                      <span className="font-medium">Members:</span> {users.filter(u => u.teamId === selectedTeamForManagement.team.id).length}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span> {selectedTeamForManagement.team.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </div>
                  </div>
                </div>

                {/* Add User to Team */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-3">Add User to Team</h4>
                  <UserInviteForm
                    onSubmit={handleInviteUser}
                    teams={[selectedTeamForManagement.team]}
                    availableScripts={availableScripts}
                    showRoleSelector={true}
                    submitLabel="Add User to Team"
                    currentUserRole={userRole}
                  />
                </div>

                {/* Team Members */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700">Team Members</h4>
                  {users.filter(u => u.teamId === selectedTeamForManagement.team.id).length > 0 ? (
                    users.filter(u => u.teamId === selectedTeamForManagement.team.id).map(user => (
                      <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium text-gray-800">{user.email}</h5>
                            <div className="flex space-x-2 mt-1">
                              <span className={getRoleBadge(user.role)}>
                                {getRoleDisplayName(user.role)}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {availableScripts[user.defaultTemplate]?.name || 'No Template'} Template
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                                            Status: {getStatusDisplayName(getActualUserStatus(user))}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmRemoveUser(user)}
                            disabled={!canManageUsers()}
                            className="text-red-600 text-xs"
                          >
                                                        Remove from Team
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm italic">No members in this team yet</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warning Dialog */}
      {showWarningDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') setShowWarningDialog(false); }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="ap-warning-dialog-title">
            <h3 id="ap-warning-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">Confirm Template Assignment</h3>
            <p className="text-gray-600 mb-4">
              {warningAction === 'updateUserTemplate' && (
                'Are you sure you want to change the template for this user? This will affect their script flow immediately.'
              )}
              {warningAction === 'bulkAssignTemplate' && (
                `Are you sure you want to assign the "${availableScripts[bulkTemplate]?.name || 'No Template'}" template to ${selectedUsers.length} user(s)? This will affect their script flow immediately.`
              )}
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => setShowWarningDialog(false)}
                className="flex-1"
              >
                                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={executeWarningAction}
                className="flex-1"
              >
                                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') setShowConfirmDialog(false); }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="ap-confirm-dialog-title">
            <h3 id="ap-confirm-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">
              {confirmAction === 'deleteTeam' ? 'Delete Team?' : 'Confirm Action'}
            </h3>
            <p className="text-gray-600 mb-4">
              {confirmAction === 'deleteTeam' && (
                'Are you sure you want to delete this team? All users will be removed from the team.'
              )}
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1"
              >
                                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={executeConfirmAction}
                className="flex-1"
              >
                                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Dialog */}
      {showRemoveConfirm && userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') { setShowRemoveConfirm(false); setUserToRemove(null); } }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="ap-remove-dialog-title">
            <h3 id="ap-remove-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">Remove User from Team?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to remove <strong>{userToRemove.email}</strong> from their team? They will lose access to all team scripts and data.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => { setShowRemoveConfirm(false); setUserToRemove(null); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={handleRemoveUserFromTeam}
                className="flex-1"
              >
                Remove from Team
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate User Confirmation Dialog */}
      {showDeactivateConfirm && userToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') { setShowDeactivateConfirm(false); setUserToDeactivate(null); } }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="ap-deactivate-dialog-title">
            <h3 id="ap-deactivate-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">Deactivate User?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to deactivate <strong>{userToDeactivate.email}</strong>? They will be signed out and unable to access the app until reactivated.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => { setShowDeactivateConfirm(false); setUserToDeactivate(null); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={executeDeactivateUser}
                className="flex-1"
              >
                Deactivate User
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPanel;







